import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { createSeries } from '@/api/series'
import { seriesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'
import type { CreateSeriesDto } from '@/api/generated/api'

/**
 * Hook to create a new series
 *
 * Handles:
 * - API call to create series
 * - Cache invalidation for series lists
 * - Navigation to new series detail page on success
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const createSeries = useCreateSeries()
 *
 * const handleSubmit = async (data) => {
 *   await createSeries.mutateAsync({
 *     name: data.name,
 *     description: data.description
 *   })
 * }
 * ```
 */
export function useCreateSeries() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateSeriesDto) => createSeries(data),
    onSuccess: newSeries => {
      // Invalidate series list queries
      queryClient.invalidateQueries({ queryKey: seriesKeys.lists() })

      // Show success toast
      toast({
        title: 'Series created',
        description: `"${newSeries.name}" has been created successfully.`,
      })

      // Navigate to the new series detail page
      navigate(`/series/${newSeries.id}`)
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to create series',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
